import { HStack, NumberInput as ChakraNumberInput, Text } from '@chakra-ui/react';
import { forwardRef, useEffect, useState } from 'react';

export type NumberInputProps = ChakraNumberInput.RootProps;

export const NumberInputRoot = forwardRef<HTMLDivElement, NumberInputProps>(function NumberInput(props, ref) {
  const { children, ...rest } = props;
  return (
    <ChakraNumberInput.Root ref={ref} {...rest}>
      {children}
    </ChakraNumberInput.Root>
  );
});

export const NumberInputField = ChakraNumberInput.Input;
export interface InputNumberProps extends Omit<NumberInputProps, 'value' | 'onChange'> {
  onChange?: (value?: number) => void;
  allowMouseWheel?: boolean;
  showStepper?: boolean;
  value?: string | number | null;
  suffix?: string;
  placeholder?: string;
}

export function InputNumber({
  value,
  placeholder,
  showStepper = false,
  allowMouseWheel = true,
  suffix,
  onChange,
  ...rest
}: Readonly<InputNumberProps>) {
  const [innerValue, setInnerValue] = useState<string>(
    Number.isNaN(Number(value)) || value === null ? '' : value!.toString(),
  );
  const handleChange = ({ value, valueAsNumber }: { value: string; valueAsNumber: number }) => {
    const valueWithPoint = value.replace(',', '.');
    setInnerValue(valueWithPoint);

    const parsed = Number(valueWithPoint);
    const isValid = !Number.isNaN(parsed);

    if (!valueWithPoint) {
      onChange?.(undefined);
    } else if (isValid && parsed === valueAsNumber) {
      onChange?.(valueAsNumber);
    }
  };

  useEffect(() => {
    setInnerValue(Number.isNaN(Number(value)) || value === null ? '' : value!.toString());
  }, [value]);

  return (
    <NumberInputRoot value={innerValue} allowMouseWheel={allowMouseWheel} onValueChange={handleChange} {...rest}>
      <HStack justifyContent="space-between" width="100%" height="100%">
        <NumberInputField
          placeholder={placeholder}
          inputMode="decimal"
          pattern="[0-9.,\-]*"
          paddingInline={0}
          flex={1}
        />
        {suffix && (
          <Text color="neutral.300" whiteSpace="nowrap" pointerEvents="none">
            {suffix}
          </Text>
        )}
      </HStack>
      {showStepper && (
        <ChakraNumberInput.Control>
          <ChakraNumberInput.IncrementTrigger />
          <ChakraNumberInput.DecrementTrigger />
        </ChakraNumberInput.Control>
      )}
    </NumberInputRoot>
  );
}
